import { c as _c } from "react/compiler-runtime";
import { useContext, useMemo } from 'react';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import useSWR from 'swr';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import type { JSONValue } from '../../../JSONBrowser';

// Shared types — keys are the path prefixes used in column strings (e.g. "body", "data", "metadata")
type Payload = Record<string, unknown>;

// Recursively extract all field paths from a JSON object
const extractFieldPaths = (obj: unknown, prefix = ''): string[] => {
  const paths: string[] = [];
  if (obj && typeof obj === 'object') {
    if (Array.isArray(obj)) {
      // For arrays, extract paths from array items using [*] notation
      // BUT don't add the bare array path itself (e.g., skip "items[*]")
      // Only add paths that access fields within array items (e.g., "items[*].sku")
      if (obj.length > 0 && obj[0] && typeof obj[0] === 'object') {
        const array_item_paths = extractFieldPaths(obj[0], prefix ? `${prefix}[*]` : '[*]');
        paths.push(...array_item_paths);
      }
    } else {
      // For objects, iterate through keys
      Object.keys(obj).forEach(key => {
        const current_path = prefix ? `${prefix}.${key}` : key;
        paths.push(current_path);

        // Recursively extract paths from nested objects/arrays
        if (obj[key] && typeof obj[key] === 'object') {
          paths.push(...extractFieldPaths(obj[key], current_path));
        }
      });
    }
  }
  return paths;
};

// Helper function to get value from nested object using dot notation with array support
const getNestedValue = (obj: unknown, parts: string[], part_index = 0): unknown => {
  if (part_index >= parts.length) {
    return obj;
  }
  if (!obj || typeof obj !== 'object') {
    return undefined;
  }
  const part = parts[part_index];
  const remaining_parts = parts.slice(part_index + 1);

  // Check if this part has array notation
  const array_wildcard_match = part.match(/^(.+)\[\*\]$/);
  if (array_wildcard_match) {
    const field_name = array_wildcard_match[1];

    // Get the array field
    if (!(field_name in obj)) {
      return undefined;
    }
    const array_value = (obj as Record<string, unknown>)[field_name];
    if (!Array.isArray(array_value)) {
      return undefined;
    }

    // If no more parts, return the array itself
    if (remaining_parts.length === 0) {
      return array_value;
    }

    // Otherwise, collect values from all array items
    const results: unknown[] = [];
    for (const item of array_value) {
      const item_value = getNestedValue(item, parts, part_index + 1);
      if (item_value !== undefined) {
        results.push(item_value);
      }
    }

    // Return collected results or undefined if empty
    return results.length > 0 ? results : undefined;
  } else {
    // Regular field access
    if (!(part in obj)) {
      return undefined;
    }
    const field_value = (obj as Record<string, unknown>)[part];

    // If no more parts, return the field value
    if (remaining_parts.length === 0) {
      return field_value;
    }

    // Otherwise, continue recursing
    return getNestedValue(field_value, parts, part_index + 1);
  }
};

// Calculate suggestions from payloads
const calculateSuggestions = (payloads: Payload[]): string[] => {
  const all_paths = new Set<string>();
  payloads.forEach(payload => {
    // Include every top-level payload key and expand object roots into their nested fields.
    Object.keys(payload).forEach(prop => {
      all_paths.add(prop);
      const paths = extractFieldPaths(payload[prop], prop);
      paths.forEach(path => all_paths.add(path));
    });
  });
  return Array.from(all_paths).sort();
};
const stableStringify = (value: unknown): string => {
  if (!value || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b));
  return `{${entries.map(([key, entry_value]) => `${JSON.stringify(key)}:${stableStringify(entry_value)}`).join(',')}}`;
};
const flattenValues = (value: unknown): unknown[] => {
  if (!Array.isArray(value)) {
    return [value];
  }
  return value.flatMap(flattenValues);
};

// Create getValues function for a set of payloads
const createGetValues = (payloads: Payload[]) => {
  return (field_path: string): JSONValue[] => {
    const parts = field_path.split('.');
    const values: JSONValue[] = [];
    const seen_values = new Set<string>();
    for (const payload of payloads) {
      const value = getNestedValue(payload, parts);
      if (value === undefined) {
        continue;
      }
      const field_values = field_path.includes('[*]') ? flattenValues(value) : [value];
      for (const field_value of field_values) {
        const value_key = stableStringify(field_value);
        if (!seen_values.has(value_key)) {
          seen_values.add(value_key);
          // Payloads are parsed JSON, and undefined was skipped above.
          values.push(field_value as JSONValue);
        }
      }
    }
    return values;
  };
};

// Simple nested value access matching table display behavior.
// Does NOT handle array wildcard [*] notation — treats path parts as literal keys,
// so array fields resolve to undefined just like in the table.
const getDisplayValue = (obj: unknown, parts: string[]): unknown => {
  let current = obj;
  for (const part of parts) {
    if (!current || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
};

// Calculate the maximum character width needed for a field across all payloads
// Returns width in pixels (8px per character) with min 100px and max 400px
// Uses the same value resolution as the table display so width is only computed
// for values the table can actually render.
const calculateFieldWidth = (payloads: Payload[], field_path: string): number => {
  const parts = field_path.split('.');
  let max_length = 0;
  for (const payload of payloads) {
    const value = getDisplayValue(payload, parts);
    if (value !== undefined && value !== null) {
      let value_string: string;
      if (typeof value === 'object') {
        value_string = JSON.stringify(value);
      } else {
        value_string = String(value);
      }
      max_length = Math.max(max_length, value_string.length);
    }
  }

  // Calculate width: 8px per character, min 100px, max 400px
  const calculated_width = max_length * 8;
  return Math.max(100, Math.min(400, calculated_width));
};
const useEventPayloadFieldsSuggestions = t0 => {
  const $ = _c(17);
  const {
    source_id,
    webhook_id,
    destination_id
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  if ($[0] !== destination_id || $[1] !== source_id || $[2] !== webhook_id) {
    t1 = (webhook_id || destination_id) && APIMethodKeys.events.list({
      limit: 5,
      include: "data",
      source_id: source_id || undefined,
      webhook_id: webhook_id || undefined,
      destination_id: destination_id || undefined
    });
    $[0] = destination_id;
    $[1] = source_id;
    $[2] = webhook_id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== HookdeckAPI.events) {
    t2 = () => HookdeckAPI.events.list({
      limit: 5,
      include: "data"
    });
    $[4] = HookdeckAPI.events;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data: recent_events
  } = useSWR(t1, t2);
  let t3;
  if ($[6] !== source_id) {
    t3 = source_id && APIMethodKeys.requests.list({
      limit: 5,
      include: "data",
      source_id: source_id || undefined
    });
    $[6] = source_id;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== HookdeckAPI.requests) {
    t4 = () => HookdeckAPI.requests.list({
      limit: 5,
      include: "data"
    });
    $[8] = HookdeckAPI.requests;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: recent_requests
  } = useSWR(t3, t4);
  let suggestions;
  let t5;
  if ($[10] !== recent_events?.models || $[11] !== recent_requests?.models) {
    const payloads = [...(recent_events?.models.map(_temp2) || []), ...(recent_requests?.models.map(_temp5) || [])];
    suggestions = calculateSuggestions(payloads);
    t5 = createGetValues(payloads);
    $[10] = recent_events?.models;
    $[11] = recent_requests?.models;
    $[12] = suggestions;
    $[13] = t5;
  } else {
    suggestions = $[12];
    t5 = $[13];
  }
  const getValues = t5;
  let t6;
  if ($[14] !== getValues || $[15] !== suggestions) {
    t6 = {
      suggestions,
      getValues
    };
    $[14] = getValues;
    $[15] = suggestions;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};

// Hook that calculates suggestions from pre-fetched list data models
export const useFieldsSuggestions = t0 => {
  const $ = _c(18);
  const {
    entity,
    models
  } = t0;
  let t1;
  if (entity === "outpost_events") {
    let t2;
    if ($[0] !== models) {
      t2 = models.filter(_temp6).map(_temp7);
      $[0] = models;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = t2;
  } else {
    if (entity === "outpost_attempts") {
      let t2;
      if ($[2] !== models) {
        t2 = models.filter(_temp8).map(_temp9);
        $[2] = models;
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      t1 = t2;
    } else {
      if (entity === "events") {
        let t2;
        if ($[4] !== models) {
          t2 = models.filter(_temp10).map(_temp12);
          $[4] = models;
          $[5] = t2;
        } else {
          t2 = $[5];
        }
        t1 = t2;
      } else {
        let t2;
        if ($[6] !== models) {
          t2 = models.filter(_temp13).map(_temp16);
          $[6] = models;
          $[7] = t2;
        } else {
          t2 = $[7];
        }
        t1 = t2;
      }
    }
  }
  const payloads = t1;
  let t2;
  if ($[8] !== payloads) {
    t2 = calculateSuggestions(payloads);
    $[8] = payloads;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const suggestions = t2;
  let t3;
  if ($[10] !== payloads) {
    t3 = createGetValues(payloads);
    $[10] = payloads;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const getValues = t3;
  let t4;
  if ($[12] !== payloads) {
    t4 = field_path => calculateFieldWidth(payloads, field_path);
    $[12] = payloads;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const getFieldWidth = t4;
  let t5;
  if ($[14] !== getFieldWidth || $[15] !== getValues || $[16] !== suggestions) {
    t5 = {
      suggestions,
      getValues,
      getFieldWidth
    };
    $[14] = getFieldWidth;
    $[15] = getValues;
    $[16] = suggestions;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
export default useEventPayloadFieldsSuggestions;
function _temp(t0) {
  const [key_0, value] = t0;
  return [key_0.toLowerCase(), value];
}
function _temp2(event) {
  return {
    headers: Object.fromEntries(Object.entries(event.data.headers || {}).filter(t0 => {
      const [key] = t0;
      return !event.data.appended_headers.includes(key);
    }).map(_temp)),
    body: event.data.body,
    query: event.data.parsed_query,
    path: event.data.path
  };
}
function _temp3(t0) {
  const [key_1] = t0;
  return key_1.toLowerCase() !== "x-hookdeck-original-ip";
}
function _temp4(t0) {
  const [key_2, value_0] = t0;
  return [key_2.toLowerCase(), value_0];
}
function _temp5(request) {
  return {
    headers: Object.fromEntries(Object.entries(request.data?.headers || {}).filter(_temp3).map(_temp4)),
    body: request.data?.body,
    query: request.data?.parsed_query,
    path: request.data?.path
  };
}
function _temp6(model) {
  return model.data != null;
}
function _temp7(event) {
  return {
    data: event.data,
    metadata: event.metadata
  };
}
function _temp8(model_0) {
  return model_0.event?.data != null;
}
function _temp9(attempt) {
  return {
    data: attempt.event.data,
    metadata: attempt.event.metadata
  };
}
function _temp10(model_1) {
  return model_1.data;
}
function _temp11(t0) {
  const [key_0, value] = t0;
  return [key_0.toLowerCase(), value];
}
function _temp12(event_0) {
  return {
    headers: Object.fromEntries(Object.entries(event_0.data.headers || {}).filter(t0 => {
      const [key] = t0;
      return !event_0.data.appended_headers?.includes(key);
    }).map(_temp11)),
    body: event_0.data.body,
    query: event_0.data.parsed_query,
    path: event_0.data.path
  };
}
function _temp13(model_2) {
  return model_2.data;
}
function _temp14(t0) {
  const [key_1] = t0;
  return key_1.toLowerCase() !== "x-hookdeck-original-ip";
}
function _temp15(t0) {
  const [key_2, value_0] = t0;
  return [key_2.toLowerCase(), value_0];
}
function _temp16(request) {
  return {
    headers: Object.fromEntries(Object.entries(request.data.headers || {}).filter(_temp14).map(_temp15)),
    body: request.data.body,
    query: request.data.parsed_query,
    path: request.data.path
  };
}